/**
 * Labstep
 *
 * @module core/Form/Reusable/Fields
 * @desc Fields for reusable form
 */

import { FieldType } from 'labstep-web/constants/form';
import { FormErrorMessage as Message } from 'labstep-web/core/Form/ReactForm/Errors/Message';
import { TextArea } from 'labstep-web/core/Form/ReactForm/TextArea';
import { FormReusableLabel } from 'labstep-web/core/Form/Reusable/Label';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import type React from 'react';
import { Form } from 'semantic-ui-react';
import type { IFormReusableFieldsProps } from './types';
import { getErrorMessage, getFieldType } from './utils';

export const FormReusableFields: React.FC<
  IFormReusableFieldsProps
> = ({
  fields,
  values,
  reset,
  submitForm,
  disabled,
  autoFocus,
  blurToggle,
  errors,
  control,
}) => {
  const onBlur = (): void => {
    if (!blurToggle) {
      return;
    }
    if (!isEmpty(errors)) {
      blurToggle();
    } else {
      submitForm();
    }
  };

  return (
    <>
      {fields.map((field, index) => {
        // Renders an action instead of a field. Use for dynamic forms.
        if (field.fieldType === 'action') {
          return field.component({ values, reset });
        }

        const { fieldType, fieldLabel, fieldExplainer } = field;

        const CurrentFieldType = getFieldType(fieldType);

        let key = field.name;
        if (field.fieldType === FieldType.DateTimePicker) {
          key = `${field.name}-${field.elementProps?.enableTime}`;
        }

        const onKeyPress = (e: KeyboardEvent): boolean => {
          if (e.key === 'Escape' && blurToggle) {
            blurToggle();
          }
          debounce(() => {
            if (
              !disabled &&
              CurrentFieldType !== TextArea &&
              e.key === 'Enter'
            ) {
              submitForm();
            }
          }, 100);
          return false;
        };
        const errorMessage = getErrorMessage(errors, field);

        return (
          <Form.Field key={key}>
            {fieldLabel && (
              <FormReusableLabel
                fieldExplainer={fieldExplainer}
                fieldLabel={fieldLabel}
              />
            )}
            <CurrentFieldType
              name={field.name}
              autoFocus={
                field.autoFocus || (!!autoFocus && index === 0)
              }
              disabled={!!(disabled || field.disabled)}
              onBlur={onBlur}
              onKeyPress={onKeyPress}
              elementProps={field.elementProps}
              control={control}
              placeholder={field.placeholder}
            />
            {errorMessage && <Message message={errorMessage} />}
          </Form.Field>
        );
      })}
    </>
  );
};
